import { useState } from "react";
import { media } from "@glitz/core";
import { styled } from "@glitz/react";
import { fontSize, spacing, Theme, red, darkGrey, black, grey, green } from "../theme";
import Input from "./basic/TextInput";
import Button from "./basic/Button";
import DeliveryOptionsWidget from "./DeliveryOptionsWidget";
import {
  isDeliverySelectionComplete,
  useShipping,
} from "../context/ShippingContext";

type Field =
  | "firstName"
  | "lastName"
  | "address"
  | "city"
  | "phone"
  | "email"
  | "zipCode";

type Errors = Partial<Record<Field, string>>;

type Phase = "zip" | "delivery" | "contact";

const normalizeSwedishZip = (value: string) => value.replace(/\s+/g, "").trim();
const isValidSwedishZip = (value: string) =>
  /^\d{5}$/.test(normalizeSwedishZip(value));
const isValidEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
const normalizePhone = (value: string) => value.replace(/[^\d+]/g, "").trim();
const isValidPhone = (value: string) => {
  const v = normalizePhone(value);
  if (!v || !/^\+?\d+$/.test(v)) return false;
  const digits = v.replace(/\D/g, "");
  return digits.length >= 8 && digits.length <= 15;
};

type Props = {
  onContinue: () => void;
};

/**
 * Kassans fraktsteg (TMA) — ett steg i taget:
 * 1) Postnummer → 2) Leveranssätt (+ ombud/box) → 3) Kontakt
 * Färdiga steg fälls ihop till sammanfattning med "Ändra".
 */
const TmaCheckoutShippingStep = ({ onContinue }: Props) => {
  const {
    shipping,
    deliverySelection,
    setDeliverySelection,
    setShipping,
    updateShippingField,
  } = useShipping();

  const [phase, setPhase] = useState<Phase>(
    isValidSwedishZip(shipping.postcode) ? "delivery" : "zip"
  );
  const [zipCode, setZipCode] = useState(shipping.postcode);
  const [firstName, setFirstName] = useState(shipping.first_name);
  const [lastName, setLastName] = useState(shipping.last_name);
  const [address, setAddress] = useState(shipping.address_1);
  const [city, setCity] = useState(shipping.city);
  const [phone, setPhone] = useState(shipping.phone);
  const [email, setEmail] = useState(shipping.email);
  const [errors, setErrors] = useState<Errors>({});
  const [formError, setFormError] = useState("");

  const postalCode = normalizeSwedishZip(zipCode);
  const zipOk = isValidSwedishZip(postalCode);
  const deliveryOk = isDeliverySelectionComplete(deliverySelection);
  const needsHomeAddress = deliverySelection?.type === "home";
  const isStorePickup = deliverySelection?.type === "store_pickup";
  const isServicePointPickup = Boolean(deliverySelection?.requiresServicePoint);

  const clearFieldError = (field: Field) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
    if (formError) setFormError("");
  };

  const onZipChange = (value: string) => {
    setZipCode(value);
    clearFieldError("zipCode");
    const normalized = normalizeSwedishZip(value);
    if (/^\d{5}$/.test(normalized)) {
      updateShippingField("postcode", normalized);
    }
  };

  const goToDelivery = () => {
    if (!zipOk) {
      setErrors({ zipCode: "Ange ett giltigt postnummer (5 siffror)" });
      return;
    }
    updateShippingField("postcode", postalCode);
    setPhase("delivery");
  };

  const editZip = () => {
    setDeliverySelection(null);
    setPhase("zip");
    setFormError("");
  };

  const editDelivery = () => {
    setPhase("delivery");
    setFormError("");
  };

  const validate = (): boolean => {
    const next: Errors = {};

    if (!zipOk) {
      next.zipCode = "Ange ett giltigt postnummer (5 siffror)";
    }
    if (!deliveryOk) {
      setFormError(
        deliverySelection?.requiresServicePoint
          ? "Välj leveranssätt och utlämningsställe."
          : "Välj leveranssätt."
      );
      setErrors(next);
      return false;
    }

    if (!firstName.trim()) next.firstName = "Ange förnamn";
    if (!lastName.trim()) next.lastName = "Ange efternamn";

    if (!email.trim()) next.email = "Ange e-post";
    else if (!isValidEmail(email)) next.email = "Ange en giltig e-postadress";

    if (!phone.trim()) next.phone = "Ange telefonnummer";
    else if (!isValidPhone(phone))
      next.phone = "Ange ett giltigt telefonnummer";

    if (needsHomeAddress) {
      if (!address.trim()) next.address = "Ange gatuadress";
      if (!city.trim()) next.city = "Ange ort";
    }

    setErrors(next);
    const ok = Object.keys(next).length === 0;
    setFormError(ok ? "" : "Kontrollera fälten markerade i formuläret.");
    return ok;
  };

  const onClickNext = () => {
    if (!validate() || !deliverySelection) return;

    const normalizedZip = normalizeSwedishZip(zipCode);

    setShipping({
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      address_1: isServicePointPickup
        ? deliverySelection.servicePointStreet ||
          deliverySelection.servicePointName ||
          deliverySelection.title ||
          "Utlämningsställe"
        : isStorePickup
          ? deliverySelection.title || "Hämta i butik"
          : address.trim(),
      address_2: isServicePointPickup
        ? deliverySelection.servicePointName || ""
        : isStorePickup
          ? "Butikshämtning"
          : "",
      postcode: isServicePointPickup
        ? deliverySelection.servicePointPostalCode || normalizedZip
        : normalizedZip,
      city: isServicePointPickup
        ? deliverySelection.servicePointCity || city.trim() || "—"
        : isStorePickup
          ? city.trim() || "—"
          : city.trim(),
      state: "",
      phone: normalizePhone(phone),
      email: email.trim(),
    });

    onContinue();
  };

  const deliverySummary = deliverySelection
    ? [
        deliverySelection.title,
        deliverySelection.servicePointName,
        deliverySelection.price != null
          ? `${deliverySelection.price} kr`
          : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "";

  return (
    <Wrapper className="checkout-section">
      <Header>Leverans</Header>

      {/* —— Postnummer —— */}
      {phase !== "zip" && zipOk ? (
        <SummaryRow>
          <SummaryText>
            <SummaryLabel>Postnummer</SummaryLabel>
            <span>{postalCode}</span>
          </SummaryText>
          <ChangeBtn type="button" onClick={editZip}>
            Ändra
          </ChangeBtn>
        </SummaryRow>
      ) : null}

      {phase === "zip" ? (
        <Section>
          <SectionTitle>Postnummer</SectionTitle>
          <Input
            label="Postnummer"
            value={zipCode}
            onChange={(e) => onZipChange(e.target.value)}
            error={Boolean(errors.zipCode)}
            helpText={errors.zipCode}
            placeholder="t.ex. 111 22"
          />
          <Hint>
            Vi visar fraktalternativ utifrån postnummer. Full adress behövs
            bara vid hemleverans.
          </Hint>
          <Button as={Theme.Dark} onClick={goToDelivery} disabled={!zipOk}>
            Visa fraktalternativ
          </Button>
        </Section>
      ) : null}

      {/* —— Leveranssätt —— */}
      {phase === "contact" && deliveryOk ? (
        <SummaryRow>
          <SummaryText>
            <SummaryLabel>Leverans</SummaryLabel>
            <span>{deliverySummary}</span>
          </SummaryText>
          <ChangeBtn type="button" onClick={editDelivery}>
            Ändra
          </ChangeBtn>
        </SummaryRow>
      ) : null}

      {phase === "delivery" && zipOk ? (
        <Section>
          <SectionTitle>Leveranssätt</SectionTitle>
          <DeliveryOptionsWidget
            postalCode={postalCode}
            city={city || undefined}
          />
          {deliveryOk ? (
            <Button as={Theme.Dark} onClick={() => setPhase("contact")}>
              Fortsätt med denna leverans
            </Button>
          ) : null}
          <BackLink type="button" onClick={editZip}>
            ← Byt postnummer
          </BackLink>
        </Section>
      ) : null}

      {/* —— Kontakt —— */}
      {phase === "contact" && deliveryOk ? (
        <Section>
          <SectionTitle>Dina uppgifter</SectionTitle>
          <FormGrid>
            <Input
              label="Förnamn"
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
                clearFieldError("firstName");
              }}
              error={Boolean(errors.firstName)}
              helpText={errors.firstName}
            />
            <Input
              label="Efternamn"
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
                clearFieldError("lastName");
              }}
              error={Boolean(errors.lastName)}
              helpText={errors.lastName}
            />
            <Input
              label="E-post"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                clearFieldError("email");
              }}
              error={Boolean(errors.email)}
              helpText={errors.email}
            />
            <Input
              label="Telefonnummer"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                clearFieldError("phone");
              }}
              error={Boolean(errors.phone)}
              helpText={errors.phone}
            />

            {needsHomeAddress ? (
              <>
                <Input
                  label="Gatuadress"
                  placeholder="Gata och husnummer"
                  value={address}
                  onChange={(e) => {
                    setAddress(e.target.value);
                    clearFieldError("address");
                  }}
                  error={Boolean(errors.address)}
                  helpText={errors.address}
                />
                <Input
                  label="Ort"
                  value={city}
                  onChange={(e) => {
                    setCity(e.target.value);
                    clearFieldError("city");
                  }}
                  error={Boolean(errors.city)}
                  helpText={errors.city}
                />
              </>
            ) : (
              <PickupNote>
                {deliverySelection?.type === "store_pickup" ? (
                  <>
                    Du hämtar i butik (
                    <strong>{deliverySelection.title || "butik"}</strong>
                    ). Vi behöver ingen hemadress.
                  </>
                ) : (
                  <>
                    Paketet skickas till{" "}
                    <strong>
                      {deliverySelection?.servicePointName || "valt ställe"}
                    </strong>
                    . Vi behöver ingen hemadress.
                  </>
                )}
              </PickupNote>
            )}
          </FormGrid>

          {formError ? <FormErrorText>{formError}</FormErrorText> : null}

          <ButtonRow>
            <BackLink type="button" onClick={editDelivery}>
              ← Byt leveranssätt
            </BackLink>
            <Button as={Theme.Dark} onClick={onClickNext}>
              Nästa steg
            </Button>
          </ButtonRow>
        </Section>
      ) : null}
    </Wrapper>
  );
};

const Wrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  width: "50%",
  maxWidth: "100%",
  justifySelf: "center",
  gap: spacing.l,
  marginBottom: spacing.xxl,
  boxSizing: "border-box",
  ...media({ maxWidth: "768px" }, {
    width: "100%",
  }),
});

const Header = styled.h1({
  textAlign: "center",
  margin: { bottom: 0 },
});

const Section = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: spacing.m,
});

const SectionTitle = styled.h2({
  fontSize: fontSize.m,
  margin: { xy: 0 },
});

const Hint = styled.p({
  margin: { xy: 0 },
  fontSize: fontSize.s,
  color: darkGrey,
});

const SummaryRow = styled.div({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: spacing.m,
  padding: { xy: spacing.m },
  border: { xy: { style: "solid", color: grey, width: "1px" } },
  borderRadius: "8px",
  backgroundColor: "#fafafa",
});

const SummaryText = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: spacing.xxs,
  fontSize: fontSize.s,
  color: black,
  minWidth: 0,
});

const SummaryLabel = styled.span({
  fontSize: fontSize.xs,
  color: darkGrey,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
});

const ChangeBtn = styled.button({
  flexShrink: 0,
  border: { xy: { style: "none" } },
  backgroundColor: "transparent",
  color: green,
  fontSize: fontSize.s,
  fontWeight: 600,
  cursor: "pointer",
  padding: { xy: spacing.xs },
  textDecoration: "underline",
});

const BackLink = styled.button({
  alignSelf: "flex-start",
  border: { xy: { style: "none" } },
  backgroundColor: "transparent",
  color: darkGrey,
  fontSize: fontSize.s,
  cursor: "pointer",
  padding: { xy: 0 },
  textDecoration: "underline",
});

const ButtonRow = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: spacing.m,
  alignItems: "stretch",
});

const FormGrid = styled.div({
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: spacing.m,
  width: "100%",
});

const PickupNote = styled.div({
  fontSize: fontSize.s,
  color: darkGrey,
  padding: { xy: spacing.s },
  backgroundColor: "#f7f7f7",
  borderRadius: "8px",
});

const FormErrorText = styled.div({
  fontSize: fontSize.s,
  color: red,
  fontWeight: 500,
  textAlign: "center",
});

export default TmaCheckoutShippingStep;
